import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import styled, { useTheme } from 'styled-components'
import type { Candle } from 'utils/stocks/candles'
import { Skeleton } from './shared'

/**
 * A candlestick chart, drawn by us.
 *
 * The obvious alternative was an iframe from a chart provider, and it was
 * rejected for three reasons that all point the same way: an iframe cannot be
 * indexed, cannot be themed, and makes the most valuable part of the page
 * somebody else's uptime. What is here is SVG and arithmetic — no charting
 * library either, because the whole job is a linear scale and a rectangle per
 * candle, and a dependency for that is a dependency to keep updated forever.
 */

const Frame = styled.div`
  position: relative;
  width: 100%;
  user-select: none;
`

const Empty = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 320px;
  border: 1px dashed ${({ theme }) => theme.colors.gray8};
  border-radius: 12px;
  color: ${({ theme }) => theme.colors.gray6};
  font-size: 13px;
  text-align: center;
  padding: 0 24px;
`

const Tooltip = styled.div<{ $left: number }>`
  position: absolute;
  top: 8px;
  left: ${({ $left }) => $left}px;
  transform: translateX(-50%);
  pointer-events: none;
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  background: ${({ theme }) => theme.colors.gray11};
  color: ${({ theme }) => theme.colors.gray1};
  font-size: 11px;
  line-height: 1.5;
  white-space: nowrap;
  z-index: 2;
`

const Loading = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
  padding: 0 8px;
  overflow: hidden;

  /* The bars share the frame's whole width, so the placeholder occupies the
     space the chart will, rather than huddling on the left. */
  > * {
    flex: 1 1 0;
    min-width: 0;
  }
`

/** A skyline that reads as "a chart is coming", in percentages of the frame. */
const LOADING_BARS = [38, 52, 44, 61, 55, 72, 64, 49, 58, 70, 63, 77, 68, 54, 62, 46, 57, 66, 50, 41]

const PADDING = { top: 14, right: 62, bottom: 22, left: 6 }
const VOLUME_SHARE = 0.18

function useElementWidth<T extends HTMLElement>(): [React.RefObject<T>, number] {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const node = ref.current
    if (!node) return undefined
    const measure = () => setWidth(node.clientWidth)
    measure()
    // A chart sized once is a chart that is wrong on the next rotation.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure)
    observer?.observe(node)
    window.addEventListener('resize', measure)
    return () => {
      observer?.disconnect()
      window.removeEventListener('resize', measure)
    }
  }, [])
  return [ref, width]
}

/**
 * Round numbers a person recognises, at roughly the spacing asked for. A scale
 * labelled 326.4137 is a scale nobody reads.
 */
export function niceTicks(min: number, max: number, count = 5): number[] {
  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return []
  const raw = (max - min) / Math.max(1, count)
  const magnitude = 10 ** Math.floor(Math.log10(raw))
  const step = [1, 2, 2.5, 5, 10].map((m) => m * magnitude).find((s) => s >= raw) ?? magnitude * 10
  const ticks: number[] = []
  for (let value = Math.ceil(min / step) * step; value <= max; value += step) {
    ticks.push(Number(value.toFixed(10)))
  }
  return ticks
}

function percentile(sorted: number[], fraction: number): number {
  if (sorted.length === 0) return 0
  const index = Math.min(sorted.length - 1, Math.max(0, Math.round((sorted.length - 1) * fraction)))
  return sorted[index]
}

/**
 * The price range the chart is drawn against.
 *
 * Normally it is simply the lowest low to the highest high with a little room.
 * The exception is what a freshly seeded pool leaves behind: TSLAc's first
 * hours carried prints at 600 and at 300 against a share that has traded near
 * 364 ever since, and scaling to those two flattens a week of real trading into
 * a horizontal line. A chart nobody can read is not more honest for containing
 * everything.
 *
 * So when the extremes are far outside the body of the series — measured, not
 * assumed: the 2nd-to-98th percentile band — the scale is taken from the band
 * instead and the outlying wick runs off the edge, clipped. It is still drawn,
 * it still ends the series where it ends, and hovering it still reports its
 * true open, high, low and close. Nothing is hidden; the axis simply stops
 * being decided by two prints.
 */
export function priceScale(candles: Candle[]): { low: number; high: number } {
  const lows = candles.map((c) => c.low).sort((a, b) => a - b)
  const highs = candles.map((c) => c.high).sort((a, b) => a - b)
  const min = lows[0] ?? 0
  const max = highs[highs.length - 1] ?? 0
  const trimmedMin = percentile(lows, 0.02)
  const trimmedMax = percentile(highs, 0.98)
  const fullSpan = max - min
  const trimmedSpan = trimmedMax - trimmedMin
  /*
   * Three times is the threshold, and it is deliberately generous: a volatile
   * week must keep its full range, and only a series whose extremes dwarf its
   * body — which is what a seeding artefact looks like — is trimmed.
   */
  const useTrimmed = trimmedSpan > 0 && fullSpan > trimmedSpan * 3
  const lowBound = useTrimmed ? trimmedMin : min
  const highBound = useTrimmed ? trimmedMax : max
  // A flat market must not become a division by zero and a chart of one line.
  const pad = (highBound - lowBound || Math.max(highBound * 0.01, 0.0001)) * 0.08
  return { low: lowBound - pad, high: highBound + pad }
}

export function formatPrice(value: number): string {
  const digits = value >= 1000 ? 0 : value >= 10 ? 2 : 4
  return value.toLocaleString('en-US', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

function formatTime(seconds: number, span: number): string {
  const date = new Date(seconds * 1000)
  if (span <= 3 * 24 * 3600) {
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  }
  return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short' })
}

interface Props {
  candles: Candle[]
  height?: number
  /** Shown in the middle of the frame when there is nothing to draw. */
  emptyMessage?: string
  /** Still being fetched: a shape of the chart to come, never a sentence. */
  loading?: boolean
}

const CandleChart: React.FC<Props> = ({ candles, height = 340, emptyMessage, loading = false }) => {
  const theme = useTheme()
  const [ref, width] = useElementWidth<HTMLDivElement>()
  const [hover, setHover] = useState<number | null>(null)

  const geometry = useMemo(() => {
    if (candles.length === 0 || width === 0) return null
    const plotWidth = Math.max(1, width - PADDING.left - PADDING.right)
    const volumeHeight = height * VOLUME_SHARE
    const plotHeight = Math.max(1, height - PADDING.top - PADDING.bottom - volumeHeight)
    const { low, high } = priceScale(candles)
    const maxVolume = Math.max(...candles.map((c) => c.volume), 1)
    const slot = plotWidth / candles.length
    return {
      plotWidth,
      plotHeight,
      volumeHeight,
      low,
      high,
      slot,
      bodyWidth: Math.max(1, Math.min(14, slot * 0.66)),
      x: (index: number) => PADDING.left + slot * (index + 0.5),
      y: (price: number) => PADDING.top + ((high - price) / (high - low)) * plotHeight,
      volumeY: (volume: number) =>
        PADDING.top + plotHeight + volumeHeight - (volume / maxVolume) * volumeHeight
    }
  }, [candles, width, height])

  const onMove = useCallback(
    (event: React.MouseEvent<SVGSVGElement>) => {
      if (!geometry) return
      const rect = event.currentTarget.getBoundingClientRect()
      const index = Math.floor((event.clientX - rect.left - PADDING.left) / geometry.slot)
      setHover(index >= 0 && index < candles.length ? index : null)
    },
    [geometry, candles.length]
  )

  if (loading) {
    /*
     * A chart's loading state is the chart's own shape, not a line of text: a
     * sentence where a chart is about to be makes the panel jump when the data
     * lands, and reads for a moment like a verdict about the market.
     */
    return (
      <Frame ref={ref}>
        <Loading style={{ height }}>
          {LOADING_BARS.map((bar, index) => (
            <Skeleton key={bar + index} $width="10px" $height={`${bar}%`} />
          ))}
        </Loading>
      </Frame>
    )
  }

  if (candles.length === 0) {
    return (
      <Frame ref={ref}>
        <Empty>{emptyMessage ?? 'No price history for this pool yet.'}</Empty>
      </Frame>
    )
  }

  const span = candles[candles.length - 1].time - candles[0].time
  const active = hover !== null ? candles[hover] : null

  return (
    <Frame ref={ref}>
      {geometry && (
        <svg
          width={width}
          height={height}
          onMouseMove={onMove}
          onMouseLeave={() => setHover(null)}
          role="img"
          aria-label="Price history"
        >
          {/* A wick outside the scale is drawn to the edge and stops there,
              rather than escaping into the volume strip and the axis labels. */}
          <defs>
            <clipPath id="mprolab-candle-plot">
              <rect
                x={PADDING.left}
                y={PADDING.top}
                width={geometry.plotWidth}
                height={geometry.plotHeight + geometry.volumeHeight}
              />
            </clipPath>
          </defs>
          {niceTicks(geometry.low, geometry.high).map((tick) => (
            <g key={tick}>
              <line
                x1={PADDING.left}
                x2={PADDING.left + geometry.plotWidth}
                y1={geometry.y(tick)}
                y2={geometry.y(tick)}
                stroke={theme.colors.gray9}
                strokeWidth={1}
              />
              <text
                x={PADDING.left + geometry.plotWidth + 8}
                y={geometry.y(tick) + 4}
                fill={theme.colors.gray6}
                fontSize={11}
              >
                {formatPrice(tick)}
              </text>
            </g>
          ))}

          <g clipPath="url(#mprolab-candle-plot)">
          {candles.map((candle, index) => {
            const rising = candle.close >= candle.open
            const colour = rising ? theme.colors.green : theme.colors.red
            const x = geometry.x(index)
            const top = geometry.y(Math.max(candle.open, candle.close))
            const bottom = geometry.y(Math.min(candle.open, candle.close))
            return (
              <g key={candle.time}>
                <line
                  x1={x}
                  x2={x}
                  y1={geometry.y(candle.high)}
                  y2={geometry.y(candle.low)}
                  stroke={colour}
                  strokeWidth={1}
                />
                <rect
                  x={x - geometry.bodyWidth / 2}
                  y={top}
                  width={geometry.bodyWidth}
                  /* A doji closes where it opened; without a floor it vanishes. */
                  height={Math.max(1, bottom - top)}
                  fill={colour}
                  opacity={hover === null || hover === index ? 1 : 0.55}
                />
                <rect
                  x={x - geometry.bodyWidth / 2}
                  y={geometry.volumeY(candle.volume)}
                  width={geometry.bodyWidth}
                  height={Math.max(
                    0,
                    PADDING.top + geometry.plotHeight + geometry.volumeHeight - geometry.volumeY(candle.volume)
                  )}
                  fill={colour}
                  opacity={0.28}
                />
              </g>
            )
          })}
          </g>

          {hover !== null && (
            <line
              x1={geometry.x(hover)}
              x2={geometry.x(hover)}
              y1={PADDING.top}
              y2={PADDING.top + geometry.plotHeight + geometry.volumeHeight}
              stroke={theme.colors.cyan}
              strokeDasharray="3 3"
              strokeWidth={1}
            />
          )}

          {[0, Math.floor(candles.length / 2), candles.length - 1].map((index) => (
            <text
              key={`t-${index}`}
              x={geometry.x(index)}
              y={height - 6}
              fill={theme.colors.gray6}
              fontSize={11}
              textAnchor={index === 0 ? 'start' : index === candles.length - 1 ? 'end' : 'middle'}
            >
              {formatTime(candles[index].time, span)}
            </text>
          ))}
        </svg>
      )}
      {active && geometry && (
        <Tooltip $left={Math.min(Math.max(geometry.x(hover as number), 90), width - 90)}>
          <div>{new Date(active.time * 1000).toLocaleString('en-GB')}</div>
          <div>
            O {formatPrice(active.open)} · H {formatPrice(active.high)} · L {formatPrice(active.low)} · C{' '}
            {formatPrice(active.close)}
          </div>
          <div>Vol ${Math.round(active.volume).toLocaleString('en-US')}</div>
        </Tooltip>
      )}
    </Frame>
  )
}

export default CandleChart
